import { inject, Injectable, signal, Signal } from '@angular/core';
import { branding } from '../config/branding';
import { LoggerService } from './logger.service';
import { UserStorageService } from './user-storage.service';

/**
 * Available in every translated string as {appName}/{appShortName}/
 * {appTagline} without callers needing to pass them explicitly. Explicit
 * `params` passed to translate() take precedence if a key ever collides.
 */
const BRANDING_PARAMS: Record<string, string> = {
  appName: branding.appName,
  appShortName: branding.appShortName,
  appTagline: branding.appTagline,
  appDescription: branding.appDescription,
};

export type TranslationData = Record<string, string>;

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private log = inject(LoggerService).getLogger('translation');
  private userStorage = inject(UserStorageService);
  private translations = new Map<string, string>();
  private currentLocale = signal<string>('en');
  private debugMode = false;

  /**
   * Get current language as a readonly signal
   */
  get currentLanguage(): Signal<string> {
    return this.currentLocale.asReadonly();
  }

  constructor() {
    // Check if debug mode is enabled (global setting, not user-scoped)
    this.debugMode = this.userStorage.getGlobalTypedItem<boolean>('translation_debug') ?? false;
  }

  /**
   * Load translation file for the specified locale
   * Implements fallback chain: specific locale → base language → default
   */
  async loadLanguage(locale: string): Promise<void> {
    const normalizedLocale = this.normalizeLocale(locale);
    const baseLanguage = this.getBaseLanguage(normalizedLocale);

    this.log.debug('loadLanguage called with:', locale);
    this.log.debug('Normalized locale:', normalizedLocale);
    this.log.debug('Base language:', baseLanguage);

    try {
      // Try to load specific locale first (e.g., en-us)
      this.log.debug('Attempting to fetch:', normalizedLocale);
      await this.fetchTranslations(normalizedLocale);
      this.currentLocale.set(normalizedLocale);
      this.storeLanguagePreference(normalizedLocale);
      this.log.debug('Successfully loaded:', normalizedLocale);
      return;
    } catch (error) {
      this.log.warn('Failed to load', normalizedLocale, error);
      // Fallback to base language if specific locale not found
      if (normalizedLocale !== baseLanguage) {
        try {
          this.log.debug('Falling back to base language:', baseLanguage);
          await this.fetchTranslations(baseLanguage);
          this.currentLocale.set(baseLanguage);
          this.storeLanguagePreference(baseLanguage);
          this.log.debug('Successfully loaded:', baseLanguage);
          return;
        } catch (baseError) {
          // Fall through to default
        }
      }

      // Fallback to default language (en)
      if (baseLanguage !== 'en') {
        try {
          await this.fetchTranslations('en');
          this.currentLocale.set('en');
          this.storeLanguagePreference('en');
          return;
        } catch (defaultError) {
          // FATAL ERROR: App cannot start without translations
          const error = new Error(
            `Failed to load translation files. Unable to load '${normalizedLocale}', '${baseLanguage}', or default 'en' translations. App cannot start without translations.`
          );
          this.log.error('FATAL: Translation loading failed completely', error);
          throw error;
        }
      } else {
        // FATAL ERROR: App cannot start without translations
        const error = new Error(
          `Failed to load default 'en' translation file. App cannot start without translations.`
        );
        this.log.error('FATAL: Default translation loading failed', error);
        throw error;
      }
    }
  }

  /**
   * Translate a key to its localized string
   * Returns the key itself if translation not found
   */
  translate(key: string, params?: Record<string, string>): string {
    let translation = this.translations.get(key);

    if (!translation) {
      if (this.debugMode) {
        this.log.warn(`Translation missing: "${key}"`);
      }
      translation = key;
    }

    // Apply parameter substitution — branding params are always available,
    // explicit params (if any) take precedence on key collision.
    const allParams = { ...BRANDING_PARAMS, ...params };
    if (translation) {
      Object.entries(allParams).forEach(([paramKey, value]) => {
        translation = translation!.replace(`{${paramKey}}`, value);
      });
    }

    return translation;
  }

  /**
   * Get current language locale string
   */
  getCurrentLanguage(): string {
    return this.currentLocale();
  }

  /**
   * Set language and reload translations
   */
  async setLanguage(locale: string): Promise<void> {
    await this.loadLanguage(locale);
  }

  /**
   * Get list of available languages (files in /i18n/)
   * For now, returns hardcoded list - can be made dynamic in future
   */
  getAvailableLanguages(): string[] {
    return ['en', 'en-us', 'es', 'fr'];
  }

  /**
   * Check if translation exists for a key
   */
  hasTranslation(key: string): boolean {
    return this.translations.has(key);
  }

  /**
   * Fetch and load translation file from server
   */
  private async fetchTranslations(locale: string): Promise<void> {
    const response = await fetch(`/i18n/${locale}.json`);

    if (!response.ok) {
      throw new Error(`Failed to load translations for ${locale}: ${response.status}`);
    }

    const data: TranslationData = await response.json();

    // Clear existing translations and load new ones
    this.translations.clear();
    Object.entries(data).forEach(([key, value]) => {
      this.translations.set(key, value);
    });
  }

  /**
   * Normalize locale string (en_US → en-us)
   */
  private normalizeLocale(locale: string): string {
    return locale.toLowerCase().replace('_', '-');
  }

  /**
   * Get base language from locale (en-us → en)
   */
  private getBaseLanguage(locale: string): string {
    return locale.split('-')[0];
  }

  /**
   * Store language preference in localStorage
   * Saves to BOTH user-scoped (if authenticated) and global (for fallback)
   */
  private storeLanguagePreference(locale: string): void {
    // Save to both user-scoped AND global storage
    // Global fallback allows language to work before auth completes
    this.userStorage.setItemBoth('user_language', locale);
  }

  /**
   * Get stored language preference or default to en
   * Tries user-scoped first, then global, then defaults to 'en'
   * This allows language to work before authentication completes
   */
  getPreferredLanguage(): string {
    // Try user-scoped first, then global, using the built-in fallback helper
    const stored = this.userStorage.getItemWithGlobalFallback('user_language');
    if (stored) {
      return stored;
    }

    // Default to en (ignore browser language)
    return 'en';
  }
}
